// 用户模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { UserState, UserActions, User, UnpaidOrderItem } from "../types";
import { LoginResponse, RegisterResponse } from "../types/auth.types";
import { userService } from "../services";
import { BaseCache } from "../../base/stores";
// 直接导入，避免走 src/index.ts 反向拉起整套 hooks barrel，造成循环依赖
import utils from "../../../utils";
import { getRechargePage } from "../../../api/records";
import { useStartupStore } from "../../startup/stores";
import { SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM } from "../../../constants/firstRechargeStagedSession";

// 创建用户模块专用的缓存实例
const userCache = new BaseCache({
  defaultTTL: 3 * 60 * 1000, // 3分钟
  maxSize: 30,
  enableLogging: true,
});

// 初始状态 - 从本地存储恢复用户信息
const getInitialUserInfo = (): User | null => {
  try {
    const cachedUserInfo = utils.getUser();
    return cachedUserInfo ? JSON.parse(cachedUserInfo) : null;
  } catch (error) {
    console.warn("parse local storage user info failed:", error);
    return null;
  }
};

const initialState: UserState = {
  userInfo: getInitialUserInfo(),
  realtime: null,
  extras: null,
  userLoading: false,
  userError: null,
  loginLoading: false,
  registerLoading: false,
  loginError: null,
  registerError: null,
  unpaidOrders: [],
  cache: new Map(),
};

// 创建用户store
export const useUserStore = createWithEqualityFn<UserState & UserActions>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setUserInfo: data => {
      if (data) {
        set({ userInfo: data });
      } else {
        const cachedUserInfo = utils.getUser();
        set({
          userInfo: cachedUserInfo ? JSON.parse(cachedUserInfo) : null,
        });
      }
    },
    setUserLoading: loading => set({ userLoading: loading }),
    setUserError: error => set({ userError: error }),
    setLoginLoading: loading => set({ loginLoading: loading }),
    setLoginError: error => set({ loginError: error }),
    setRegisterLoading: loading => set({ registerLoading: loading }),
    setRegisterError: error => set({ registerError: error }),
    loadUserInfo: async opts => {
      // realtime 默认 true；仅不展示余额、也无需刷新实时态的页面(如 VIP 页进入)可传 { realtime: false } 跳过 user_realtime
      const includeRealtime = opts?.realtime !== false;
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        // user_info 纯静态存入 userInfo；余额等实时态独立存入 realtime 字段(与 userInfo 分离)。
        // 两者并行拉取，realtime 失败不阻断静态加载。返回 merged 兼容读返回值取余额的旧调用方。
        const [staticUser, realtime] = await Promise.all([
          userService.loadUserInfo(opts?.force),
          includeRealtime
            ? userService.loadUserRealtime().catch(() => ({}) as Partial<User>)
            : Promise.resolve({} as Partial<User>),
        ]);
        // VIP 升级信号处理：真正的升级(vipLevel 落库)发生在后端 user_realtime 的 checkLevel 里，
        // 而 user_info 读的是 DB vipLevel。二者在此并行请求，user_info 的后端读可能早于 user_realtime
        // 的 checkLevel 落库 → 返回旧等级；即便 force 也只是绕过前端缓存、挡不住这个后端并发时序。
        // 故只要本次 realtime 报了 upgraded，就在 Promise.all 之后(此时 checkLevel 已落库)清缓存并
        // 重拉一次静态——重拉一定读到升级后的新 vipLevel。不加 !opts?.force：force 路径同样会踩此竞态。
        // upgraded 为一次性信号，正常只在升级那次触发一次重拉。
        let staticResult = staticUser;
        if (
          includeRealtime &&
          (realtime as { upgraded?: boolean })?.upgraded === true
        ) {
          userService.clearUserInfoCache();
          try {
            staticResult = await userService.loadUserInfo(true);
          } catch {
            /* 强制重拉失败则退回原 staticUser，不阻断主流程 */
          }
        }
        // 合并而非整体替换：保留红包/签到等由专项接口(redpacket_status/user_checkin)写入 userInfo 的字段
        const mergedStatic = {
          ...(get().userInfo as any),
          ...staticResult,
        } as User;
        setUserInfo(mergedStatic);
        // includeRealtime=false 时不刷 realtime，保留已有实时态(避免清空余额)
        if (includeRealtime) {
          get().setRealtime(realtime);
        }
        // 持久化合并快照供首屏显示；返回值只给静态(余额统一从 useUserDataFlattened 读)
        utils.setUser({
          ...mergedStatic,
          ...(get().extras || {}),
          ...(get().realtime || {}),
        } as User);
        setUserLoading(false);

        await get().fetchUnpaidOrders();

        return staticUser;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load user info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },

    loadGuestInfo: async () => {
      const { setUserLoading, setUserError, setUserInfo } = get();
      setUserLoading(true);
      setUserError(null);
      try {
        const guest = await userService.loadGuestInfo();
        // 合并保留专项接口(redpacket_status)为游客写入的红包字段
        const user = { ...(get().userInfo as any), ...guest } as User;
        setUserInfo(user);
        utils.setUser(user);
        setUserLoading(false);
        await get().fetchUnpaidOrders();
        return user;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "load guest info failed";
        setUserError(errorMessage);
        setUserLoading(false);
        throw error;
      }
    },
    // 余额/任务等实时态独立存入 realtime 字段，不混入 userInfo
    setRealtime: data =>
      set(state => ({ realtime: { ...(state.realtime || {}), ...data } })),
    // 红包/签到等专项字段独立存入 extras，不回写 userInfo，避免覆盖 user_info 独占字段
    setExtras: data =>
      set(state => ({ extras: { ...(state.extras || {}), ...data } })),
    loadUserRealtime: async () => {
      const partial = await userService.loadUserRealtime();
      get().setRealtime(partial);
      // VIP 升级：独立调用 loadUserRealtime(投注/领奖/退游戏后)是最常见的升级感知路径。
      // 仅清缓存不够——store 里的 userInfo.vipLevel 不会更新，UI 会一直显示旧等级直到下次 loadUserInfo。
      // 故此处清缓存后立即重拉 user_info(此时 checkLevel 已在本 realtime 请求内落库)，把新 vipLevel 写回。
      if ((partial as any)?.upgraded === true) {
        userService.clearUserInfoCache();
        try {
          const freshStatic = await userService.loadUserInfo(true);
          get().setUserInfo({
            ...(get().userInfo as any),
            ...freshStatic,
          } as User);
        } catch {
          /* 重拉失败不阻断：缓存已清，下次 loadUserInfo 仍会拉到新等级 */
        }
      }
      // 持久化合并快照(放在 vipLevel 更新之后，快照也带上新等级)
      utils.setUser({
        ...(get().userInfo as any),
        ...(get().extras || {}),
        ...(get().realtime as any),
      });
      return partial;
    },
    loadRedpacketStatus: async () => {
      const partial = await userService.loadRedpacketStatus();
      // 存入 extras 而非回写 userInfo，避免覆盖 user_info 独占字段(如 hasLoginPassword)
      get().setExtras(partial);
      utils.setUser({
        ...(get().userInfo as any),
        ...(get().extras || {}),
        ...(get().realtime || {}),
      });
      return partial;
    },
    loadUserCheckin: async () => {
      const partial = await userService.loadUserCheckin();
      get().setExtras(partial);
      utils.setUser({
        ...(get().userInfo as any),
        ...(get().extras || {}),
        ...(get().realtime || {}),
      });
      return partial;
    },
    updateUserBalance: balance =>
      set(state => {
        const prev = state.realtime || {};
        const prevDetail = prev.balanceDetail || {};
        // 账户余额是同源的三个视角(balance / balanceDetail.balance / balanceDetail.accountBalance),
        // 乐观更新时必须一起更新, 否则提现页(读 balanceDetail.balance)看不到变化。
        // availableBalance(可提)受稽核限制、不等于账户余额, 不做乐观猜测, 交由 loadUserRealtime 刷新。
        return {
          realtime: {
            ...prev,
            balance,
            balanceDetail: {
              ...prevDetail,
              balance,
              accountBalance: balance,
            } as User["balanceDetail"],
          },
        };
      }),
    refreshUserInfo: () => set({ userInfo: get().userInfo }),
    isAuthenticated: () => get().userInfo !== null && !!get().userInfo?.userId,
    setUnpaidOrders: orders => set({ unpaidOrders: orders }),

    logout: () => {
      try {
        sessionStorage.removeItem(SESSION_PENDING_STAGED_FIRST_DEPOSIT_CLAIM);
      } catch {
        /* ignore */
      }
      userService.logout();
    },

    fetchUnpaidOrders: async () => {
      const startupData = useStartupStore.getState().startupData;
      if (!startupData?.paymentVoucherTemplate?.enabled) {
        set({ unpaidOrders: [] });
        return;
      }
      try {
        const resp = await getRechargePage({
          query: { pageNo: 1, pageSize: 10, status: "PENDING" },
        });
        const page = resp?.page ?? resp?.data?.page;
        const content: any[] = page?.content ?? [];
        const items: UnpaidOrderItem[] = content
          .filter((r: any) => !r.subCurrencyFlag)
          .map((r: any) => {
            const offline = r.offlineTokenWalletData;
            const voucherImgs =
              Array.isArray(r.paymentVoucherImgs) &&
              r.paymentVoucherImgs.length > 0
                ? r.paymentVoucherImgs
                : Array.isArray(offline?.certificateImgs) &&
                    offline.certificateImgs.length > 0
                  ? offline.certificateImgs
                  : undefined;
            const numIds =
              Array.isArray(r.numberIds) && r.numberIds.length > 0
                ? r.numberIds
                : Array.isArray(offline?.numberIds) &&
                    offline.numberIds.length > 0
                  ? offline.numberIds
                  : undefined;
            return {
              id: r.id,
              orderNo: r.orderNo,
              amount: r.amount,
              currencySymbol: r.currencySymbol,
              status: r.status,
              created: r.created,
              paymentVoucherImgs: voucherImgs,
              numberIds: numIds,
              rejectTemplateContent: r.rejectTemplateContent,
              subCurrencyFlag: r.subCurrencyFlag,
            };
          });
        set({ unpaidOrders: items });
      } catch (error) {
        console.warn("fetchUnpaidOrders failed:", error);
        set({ unpaidOrders: [] });
      }
    },

    setCache: (key, data, ttl) => {
      userCache.setCache(key, data, ttl);
      set({ cache: new Map(userCache["cache"]) });
    },

    getCache: key => {
      return userCache.getCache(key);
    },

    clearCache: pattern => {
      userCache.clearCache(pattern);
      set({ cache: new Map(userCache["cache"]) });
    },

    hasCache: key => {
      return userCache.hasCache(key);
    },

    getCacheKeys: () => {
      return userCache.getCacheKeys();
    },

    getCacheSize: () => {
      return userCache.getCacheSize();
    },

    cacheFirst: async (key, apiCall, ttl) => {
      return userCache.cacheFirst(key, apiCall, ttl);
    },
    login: async data => {
      const { setLoginLoading, setLoginError, setUserInfo } = get();
      setLoginLoading(true);
      setLoginError(null);
      try {
        const result: LoginResponse = await userService.login(data);
        // 身份变更：清掉上一身份/游客的 user_info 静态缓存，避免下次 loadUserInfo 用旧缓存覆盖新用户
        userService.clearUserInfoCache();
        setUserInfo(result.user);
        utils.setUser(result.user);
        utils.markEverLoggedIn();
        setLoginLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "login failed";
        setLoginError(errorMessage);
        setLoginLoading(false);
        throw error;
      }
    },
    register: async data => {
      const { setRegisterLoading, setRegisterError, setUserInfo } = get();
      setRegisterLoading(true);
      setRegisterError(null);
      try {
        const result: RegisterResponse = await userService.register(data);
        // 身份变更：清掉上一身份/游客的 user_info 静态缓存
        userService.clearUserInfoCache();
        setUserInfo(result.user);
        utils.setUser(result.user);
        utils.markEverLoggedIn();
        setRegisterLoading(false);
        return result;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "register failed";
        setRegisterError(errorMessage);
        setRegisterLoading(false);
        throw error;
      }
    },
  }))
);

// 选择器hooks
export const useUserData = () =>
  useUserStore(state => ({
    data: state.userInfo,
    loading: state.userLoading,
    error: state.userError,
  }));

export const useUserAuth = () =>
  useUserStore(state => ({
    isAuthenticated: state.isAuthenticated(),
    loginLoading: state.loginLoading,
    registerLoading: state.registerLoading,
    loadUserInfo: state.loadUserInfo,
    loadGuestInfo: state.loadGuestInfo,
    loginError: state.loginError,
    registerError: state.registerError,
    login: state.login,
    register: state.register,
    logout: state.logout,
  }));

// 扁平化的用户数据 hook - 直接导出 User 的所有属性并整合认证功能
export const useUserDataFlattened = () =>
  useUserStore(state => {
    const {
      userInfo,
      realtime,
      extras,
      userLoading,
      userError,
      loginLoading,
      registerLoading,
      loginError,
      registerError,
    } = state;

    // 如果 userInfo 为 null，返回所有属性为 undefined
    if (!userInfo) {
      return {
        // 状态信息
        loading: userLoading,
        error: userError,
        data: null,

        // 认证相关状态和方法
        isAuthenticated: false,
        loginLoading,
        registerLoading,
        loginError,
        registerError,

        // 认证相关方法
        loadUserInfo: state.loadUserInfo,
        loadUserRealtime: state.loadUserRealtime,
        loadRedpacketStatus: state.loadRedpacketStatus,
        loadUserCheckin: state.loadUserCheckin,
        login: state.login,
        register: state.register,
        logout: state.logout,
        updateUserBalance: state.updateUserBalance,
        refreshUserInfo: state.refreshUserInfo,

        // User 的所有属性，当 userInfo 为 null 时都设为 undefined
        userId: undefined,
        username: undefined,
        root: undefined,
        big: undefined,
        center: undefined,
        showEncryptData: undefined,
        out: undefined,
        token: undefined,
        selB: undefined,
        currRatio: undefined,
        currSymbol: undefined,
        createdTime: undefined,
        auth2Verify: undefined,
        email: undefined,
        phone: undefined,
        phoneChecked: undefined,
        avatar: undefined,
        balance: undefined,
        availableBalance: undefined,
        taskBonusBalance: undefined,
        balanceDetail: undefined,
        depositForWithdraw: undefined,
        registerTaskBonusBalance: undefined,
        cumulativeBetAmount: undefined,
        firstRechargeFlag: undefined,
        canPlayThirdPartyGame: undefined,
        firstRechargePopupShowPercent: undefined,
        firstRechargeRulePromoRatio: undefined,
        thirdGameWalletList: undefined,
        vipLevel: undefined,
        nextlevel: undefined,
        thirdLoginTypes: undefined,
        bind2Auth: undefined,
        firstRechargeRuleAmount: undefined,
        customRechargeRules: undefined,
        redpacket: undefined,
        betAmount: undefined,
        rechargeAmount: undefined,
        withdrawAmount: undefined,
        bonusTaskCheckInDay: undefined,
        todayCheckIn: undefined,
        upgraded: undefined,
        noticeUpdateTime: undefined,
        customRechargeRuleAmount: undefined,
        whatsapp: undefined,
        preferences: undefined,
        userCheckIn: undefined,
        redPacketReceivable: undefined,
        rechargeCount: undefined,
        dailyRechargeTasks: undefined,
        dailyRechargeAllCompleted: undefined,
        dailyRechargeTask: undefined,
        dailyBetTask: undefined,
      };
    }

    // 当 userInfo 存在时，展开所有属性（叠加 extras 红包/签到、realtime 余额，使这些字段对消费方透明）
    // 顺序：userInfo(静态基底，hasLoginPassword 等独占) → extras(红包/签到) → realtime(余额，最高优先)
    const dataMerged = {
      ...userInfo,
      ...(extras || {}),
      ...(realtime || {}),
    } as User;
    return {
      // 状态信息
      loading: userLoading,
      error: userError,
      data: dataMerged,

      // 认证相关状态和方法
      isAuthenticated: !!userInfo.userId,
      loginLoading,
      registerLoading,
      loginError,
      registerError,

      // 认证相关方法
      loadUserInfo: state.loadUserInfo,
      loadUserRealtime: state.loadUserRealtime,
      loadRedpacketStatus: state.loadRedpacketStatus,
      loadUserCheckin: state.loadUserCheckin,
      login: state.login,
      register: state.register,
      logout: state.logout,
      updateUserBalance: state.updateUserBalance,
      refreshUserInfo: state.refreshUserInfo,

      // User 的所有属性（extras 红包/签到、realtime 余额 依次覆盖 userInfo）
      ...userInfo,
      ...(extras || {}),
      ...(realtime || {}),
    };
  }, shallow);

export function useUserCache() {
  const { cache } = useUserStore(state => ({
    cache: state.cache,
  }));
  return cache;
}

export const useUnpaidOrders = () =>
  useUserStore(
    state => ({
      unpaidOrders: state.unpaidOrders,
      hasUnpaidOrders: state.unpaidOrders.length > 0,
      fetchUnpaidOrders: state.fetchUnpaidOrders,
    }),
    shallow
  );
